/**
 * AI Usage Log card — owner-only Settings card.
 *
 * Reads:
 *   GET /api/hidro-usage-log?days=7
 *   GET /api/hidro-usage-log/recent-errors
 *
 * Shows:
 *   - Headline: total calls, ok-rate, p95 latency in the last N days
 *   - Per-surface table sorted by call count (top 8)
 *   - Recent errors list (last 5) — click to inspect
 *
 * Designed so the owner can answer "where is the money going?" in
 * one glance and "is something silently degrading?" via the escalation-
 * tier counter.
 */
import { useCallback, useEffect, useState } from "react";
import { Activity, AlertTriangle, RefreshCw, TrendingUp } from "lucide-react";
import api from "../../lib/api";

const RANGE_OPTIONS = [
  { label: "24h", days: 1 },
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
];

function fmtMs(ms) {
  if (!ms) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function pct(n) {
  return `${Math.round((n || 0) * 100)}%`;
}

export default function UsageLogCard({ userRole }) {
  // Owners-only guard. Backend already enforces require_roles("owner","admin"),
  // but this hides the card entirely so it never even hits the network.
  // NOTE: the early-return MUST sit below the hook declarations to obey
  // React's rules-of-hooks (every render must call hooks in the same
  // order). Originally placed above — caused the "useState called
  // conditionally" build break of 2026-02-15.
  const isOwner = userRole === "owner" || userRole === "admin";

  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!isOwner) return;
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get(`/hidro-usage-log?days=${days}`),
        api.get("/hidro-usage-log/recent-errors?limit=5"),
      ]);
      setData(s.data);
      setErrors(e.data?.items || []);
    } catch {
      setData(null);
      setErrors([]);
    } finally {
      setLoading(false);
    }
  }, [days, isOwner]);

  useEffect(() => { if (open && isOwner) load(); }, [open, isOwner, load]);

  if (!isOwner) return null;

  return (
    <div className="hv-card p-5" data-testid="usage-log-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between gap-3 text-left"
        data-testid="usage-log-toggle"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0">
            <Activity className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold">AI usage log</h2>
            <p className="text-sm text-[#a0aabf] max-w-prose mt-1">
              Owner-only view of what Hidro is doing under the hood — how many
              calls, which surfaces, where it&apos;s slow, and which calls have
              been escalating. Useful when LLM credits are running thin.
            </p>
          </div>
        </div>
        <TrendingUp
          className={`w-4 h-4 text-[#647087] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="mt-5 space-y-5">
          {/* Range + refresh */}
          <div className="flex items-center gap-2 flex-wrap">
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.days}
                type="button"
                onClick={() => setDays(r.days)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  days === r.days
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                    : "bg-transparent border-[#2a3142] text-[#a0aabf] hover:border-emerald-500/40"
                }`}
                data-testid={`usage-range-${r.days}`}
              >
                {r.label}
              </button>
            ))}
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="ml-auto inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-[#2a3142] text-[#a0aabf] hover:border-emerald-500/40 disabled:opacity-50"
              data-testid="usage-refresh"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>

          {loading && !data ? (
            <div className="text-xs text-[#647087] py-4 text-center">Loading…</div>
          ) : !data || data.total_calls === 0 ? (
            <div className="text-sm text-[#647087] py-4 text-center" data-testid="usage-empty">
              No AI calls in the last {days === 1 ? "24 hours" : `${days} days`} yet.
              Try a Hidro chat or a document upload and refresh.
            </div>
          ) : (
            <>
              {/* Headline */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="usage-headline">
                <Stat label="Calls" value={data.total_calls.toLocaleString()} />
                <Stat
                  label="OK rate"
                  value={pct(data.ok_rate)}
                  tone={data.ok_rate >= 0.95 ? "good" : data.ok_rate >= 0.85 ? "warn" : "bad"}
                />
                <Stat label="p50 latency" value={fmtMs(data.p50_ms)} />
                <Stat label="p95 latency" value={fmtMs(data.p95_ms)} />
              </div>

              {/* Per-surface table */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.18em] text-[#647087] border-b border-[#2a3142]">
                      <th className="text-left py-2 pr-3">Surface</th>
                      <th className="text-right py-2 px-2">Calls</th>
                      <th className="text-right py-2 px-2">OK %</th>
                      <th className="text-right py-2 px-2">Escalated</th>
                      <th className="text-right py-2 px-2">p50</th>
                      <th className="text-right py-2 pl-2">p95</th>
                    </tr>
                  </thead>
                  <tbody data-testid="usage-surfaces">
                    {(data.per_surface || []).slice(0, 12).map((s) => (
                      <tr key={s.surface} className="border-b border-[#1a1f2c] last:border-0">
                        <td className="py-2 pr-3 font-mono text-[#dde3ec]">{s.surface}</td>
                        <td className="py-2 px-2 text-right text-[#dde3ec]">{s.calls}</td>
                        <td className="py-2 px-2 text-right">
                          <span
                            className={
                              s.ok_rate >= 0.95
                                ? "text-emerald-300"
                                : s.ok_rate >= 0.85
                                ? "text-amber-300"
                                : "text-rose-300"
                            }
                          >
                            {pct(s.ok_rate)}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right">
                          {s.escalated > 0 ? (
                            <span className="text-amber-300">{s.escalated}</span>
                          ) : (
                            <span className="text-[#647087]">0</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-right text-[#a0aabf]">{fmtMs(s.p50_ms)}</td>
                        <td className="py-2 pl-2 text-right text-[#a0aabf]">{fmtMs(s.p95_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Recent errors */}
              {errors.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#647087] mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Recent errors
                  </div>
                  <div className="space-y-1.5" data-testid="usage-errors">
                    {errors.map((e) => (
                      <div
                        key={e.id}
                        className="text-[11px] bg-[#0d101a] border border-rose-500/20 rounded px-3 py-2"
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="font-mono text-rose-300">{e.surface}</span>
                          <span className="text-[#647087]">{e.ts?.slice(0, 19).replace("T", " ")}</span>
                        </div>
                        <div className="text-[#a0aabf] mt-1 truncate" title={e.error}>
                          {e.error || "(no message)"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneMap = {
    good: "text-emerald-300",
    warn: "text-amber-300",
    bad: "text-rose-300",
  };
  return (
    <div className="bg-[#0d101a] border border-[#2a3142] rounded-md p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#647087]">{label}</div>
      <div className={`font-display text-xl font-bold mt-1 ${toneMap[tone] || "text-[#dde3ec]"}`}>
        {value}
      </div>
    </div>
  );
}
